/**
 * Instruments API Module
 *
 * This module provides functions for interacting with the instrument-related endpoints
 * of the WealthBee Enterprise API.
 */
import axios from "axios";
import { Instrument } from "../types";
import getServiceEndpoint from "./ServiceEndpoint";
import { buildClientHeader } from "./buildHeader";

/**
 * Retrieves all instruments from the API.
 *
 * @returns A promise that resolves to an array of Instrument objects.
 * If the request fails, returns an empty array.
 */
export const getInstruments = async (): Promise<Instrument[]> => {
  try {
    const response = await axios.get(
      `${getServiceEndpoint()}/instruments`,
      buildClientHeader()
    );
    if (response.status !== 200 || response.data === undefined) {
      console.log("Failed to retrieve instruments", response.statusText);
      return [];
    }
    return response.data ?? [];
  } catch (error) {
    console.error("Error getting instruments:", error);
  }

  return [];
};

/**
 * Retrieves a specific instrument by their ID.
 *
 * @param id - The unique identifier of the instrument to retrieve.
 * @returns A promise that resolves to an Instrument object.
 * @throws Will throw an error if the API request fails.
 */
export const getInstrumentById = async (id: number): Promise<Instrument> => {
  const response = await axios.get(
    `${getServiceEndpoint()}/instruments/${id}`,
    buildClientHeader()
  );
  return response.data;
};
